import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Typography } from '../components/common';
import Button from '../components/common/Button';
import ShuttlrBrandMark from '../components/common/ShuttlrBrandMark';
import LandingFooter from '../components/landing/LandingFooter';
import LandingLayout from '../components/landing/LandingLayout';

export const PRIVACY_LAST_UPDATED = '2 October 2026';
export const PRIVACY_CONTACT_EMAIL = 'shuttlr8@gmail.com';

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <Typography variant="h3" as="h2">
      {title}
    </Typography>
    {children}
  </section>
);

const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy — Shuttlr';
  }, []);

  return (
    <LandingLayout>
      <header className="bg-gradient-to-r from-[#0a1628] via-slate-900 to-slate-800 text-white">
        <div className="mx-auto flex max-w-app-shell items-center justify-between gap-4 px-6 py-4 sm:px-8 lg:px-10">
          <Link to="/" className="flex items-center gap-2.5 text-white">
            <ShuttlrBrandMark />
            <span className="text-lg font-bold">Shuttlr</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button as={Link} to="/login" variant="nav" size="small">
              Log in
            </Button>
            <Button as={Link} to="/signup" variant="primary" size="small">
              Sign up
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-6 py-10 sm:px-8 lg:px-10">
        <Typography variant="h1">Privacy Policy</Typography>
        <Typography variant="muted" className="mt-2">
          Last updated: {PRIVACY_LAST_UPDATED}
        </Typography>
        <Typography variant="body" className="mt-6">
          This explains how Shuttlr uses personal information on{' '}
          <a href="https://www.shuttlr.net" className="text-primary underline-offset-4 hover:underline">
            www.shuttlr.net
          </a>
          . We use it to provide your account and help clubs run sessions.
        </Typography>

        <div className="mt-10 space-y-8">
          <Section title="Who we are">
            <Typography variant="body">
              Shuttlr is a badminton club and session app. The operator of
              www.shuttlr.net is the controller of your data. Clubs also decide how
              they use member and guest records they create (for example adding a
              guest or marking a session as paid).
            </Typography>
            <Typography variant="body">
              Questions or privacy requests:{' '}
              <a
                href={`mailto:${PRIVACY_CONTACT_EMAIL}`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {PRIVACY_CONTACT_EMAIL}
              </a>
              .
            </Typography>
          </Section>

          <Section title="What we collect">
            <ul className="list-disc space-y-1 pl-5 text-base text-text-primary leading-relaxed">
              <li>Account details: name, email, password, competition category, and optional photo</li>
              <li>Club and play data: memberships, sessions, scores, and ratings</li>
              <li>Guest records a club creates (usually a name, and an email if they assign one)</li>
              <li>Session payment notes recorded by hosts (cash or bank transfer — we do not take card payments)</li>
              <li>Photos or logos you or a club upload</li>
              <li>County league results we copy from a public association website (player name as shown, sex as shown on match cards, teams, clubs, and scores)</li>
            </ul>
            <Typography variant="body">
              If you sign in with Google, we receive the name and email Google shares
              with us.
            </Typography>
          </Section>

          <Section title="How we use it">
            <Typography variant="body">
              We use this information to run your account and club sessions — sign-up,
              matchmaking, scores, ratings, and host payment tracking. We also use it
              to keep the service secure (including Captcha on login and sign-up).
              That is our lawful basis: providing the service you asked for.
            </Typography>
            <Typography variant="body">
              We do not sell your information or use advertising cookies.
            </Typography>
          </Section>

          <Section title="Who can see it">
            <Typography variant="body">
              People in your clubs can see names, photos, ratings, and match history.
              Hosts and admins can also see sign-ups and who has paid. We use
              Supabase to host accounts, data, and sign-in emails, and Google for
              optional Google sign-in and Captcha. We only share data further if the
              law requires it.
            </Typography>
          </Section>

          <Section title="County league results">
            <Typography variant="body">
              We copy public Middlesex County Badminton Association league tables,
              fixtures, and match cards into Shuttlr so anyone can browse standings
              and results. That copy includes the player name as shown on the source
              site, sex as shown on match cards, teams, clubs, and scores. We show
              this on the public league pages and on linked club pages. We do not
              collect anything the source site does not already publish.
            </Typography>
            <Typography variant="body">
              Our lawful basis is legitimate interests: helping players and clubs
              follow local league results. A club admin can link a league record to
              a Shuttlr member at that club.
            </Typography>
            <Typography variant="body">
              To have your league name removed, email{' '}
              <a
                href={`mailto:${PRIVACY_CONTACT_EMAIL}`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {PRIVACY_CONTACT_EMAIL}
              </a>
              {' '}with your name and club. We replace your name with “Withheld
              player” (match scores stay so other players’ results stay intact),
              remove any account link, and stop future imports showing your name. We
              respond within one month. League data is kept while the season is
              shown on Shuttlr.
            </Typography>
          </Section>

          <Section title="How long we keep it">
            <Typography variant="body">
              We keep records while your account is active and while a club still
              needs them for its session history. You can update your profile in
              account settings. To delete your account, email us — we do not yet
              have a delete button. We may need to keep some club history if
              removing it would break other players&apos; records.
            </Typography>
          </Section>

          <Section title="Your rights">
            <Typography variant="body">
              You can ask us for a copy of your data, to correct it, or to delete it
              where the law allows. You can also complain to the{' '}
              <a
                href="https://ico.org.uk/make-a-complaint/"
                className="text-primary underline-offset-4 hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                Information Commissioner&apos;s Office
              </a>
              .
            </Typography>
            <Typography variant="body-small">
              Accounts are for people aged 16 or over. Clubs that add younger
              guests are responsible for having the right to do so. We use cookies
              only to keep you signed in and for Captcha. Our providers may process
              data outside the UK with appropriate safeguards. We will update the
              date above if this policy changes.
            </Typography>
          </Section>
        </div>
      </main>

      <LandingFooter />
    </LandingLayout>
  );
};

export default PrivacyPolicyPage;
